import { ArrowRightOutlined, RightOutlined } from '@ant-design/icons';
import type { TableProps } from 'antd';
import { Alert, Button, Dropdown, Form, Input, Pagination, Select, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import CountryFlag from 'components/atoms/CountryFlag';
import SelectComponent, { ISelectData } from 'components/atoms/SelectComponent';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import LeiLoginlessFooter from 'components/molecules/Lei/zadostLeiLoginless/LeiLoginlessFooter';
import LeiLoginlessMenu from 'components/molecules/Lei/zadostLeiLoginless/LeiLoginlessMenu';
import LeiZadostForm from 'components/molecules/Lei/zadostLeiLoginless/LeiZadostForm';

import { preloadCountryFlags } from 'services/lei/leiCountryFlag';
import {
    GLEIF_MAX_RECORDS,
    ILeiSearchFilters,
    ILeiSearchRow,
    ILeiSearchSort,
    isLeiSearchCancelled,
    searchLeiRecords
} from 'services/lei/leiGleifSearch';

import { ETypZadosti } from 'enums/lei/leiTypZadosti';

import { leiCountrySelect } from 'data/codebooks/lei/leiCountrySelect';

import getLanguageObject from 'utils/getLanguageObject';

const { Option } = Select;

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_JURISDICTION = ['CZ'];
// how long to wait after the last filter click before firing the API request
const FILTER_DEBOUNCE_MS = 300;
// the LEI of our own LOU (CDCP) - only the LEIs it manages get the "Obnova LEI" action
const CDCP_LOU_LEI = '315700LK78Z7C0WMIL03';

// value = GLEIF API value, labelcz = translate key
const CATEGORY_OPTIONS: ISelectData[] = [
    'GENERAL',
    'BRANCH',
    'FUND',
    'SOLE_PROPRIETOR',
    'RESIDENT_GOVERNMENT_ENTITY',
    'INTERNATIONAL_ORGANIZATION'
].map(value => ({
    value,
    labelcz: `LEI_CATEGORY_${value}`
}));

// filter[registration.status]
const STATUS_OPTIONS: ISelectData[] = [
    'ISSUED',
    'LAPSED',
    'MERGED',
    'RETIRED',
    'DUPLICATE',
    'ANNULLED',
    // 'CANCELLED',
    // 'TRANSFERRED',
    'PENDING_TRANSFER',
    'PENDING_ARCHIVAL'
].map(value => ({
    value,
    labelcz: `LEI_STATUS_${value}`
}));

const statusTagColor = (status: string) => {
    if (status === 'ISSUED') return 'green';
    if (status === 'LAPSED') return 'orange';
    if (status.startsWith('PENDING')) return 'blue';
    return 'default';
};

const resultWordKey = (count: number) =>
    count === 1
        ? 'LEI_SEARCH_RESULT_ONE'
        : count >= 2 && count <= 4
          ? 'LEI_SEARCH_RESULT_FEW'
          : 'LEI_SEARCH_RESULT_MANY';

interface ISearchFormValues {
    q?: string;
    jurisdikce?: string[];
    kategorie?: string[];
    stavLei?: string[];
}

type TFilterField = 'jurisdikce' | 'kategorie' | 'stavLei';

type TChipValues = { jurisdikce: string[]; kategorie: string[]; stavLei: string[] };

interface ILeiLoginlessSearch {}

const LeiLoginlessSearch: React.FC<ILeiLoginlessSearch> = () => {
    const { language, cisStaty } = useContext(Context);
    const location = useLocation();
    const [form] = Form.useForm<ISearchFormValues>();

    const initialQ = new URLSearchParams(location.search).get('q')?.trim() ?? '';

    const [filters, setFilters] = useState<ILeiSearchFilters>({
        q: initialQ,
        jurisdikce: DEFAULT_JURISDICTION,
        kategorie: [],
        stavLei: []
    });
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
    const [sort, setSort] = useState<ILeiSearchSort>();
    const [rows, setRows] = useState<ILeiSearchRow[]>([]);
    const [total, setTotal] = useState<number>();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    // "Obnova LEI" / "Převod LEI" picked from the table - swaps the table for LeiZadostForm
    const [rowAction, setRowAction] = useState<{ lei: string; typ: ETypZadosti } | null>(null);
    // source of truth for the three filter selects - the selects themselves always render
    // empty (placeholder stays visible), the picked values show as removable chips next to them
    // and are highlighted inside the dropdown
    const [chipValues, setChipValues] = useState<TChipValues>({
        jurisdikce: DEFAULT_JURISDICTION,
        kategorie: [],
        stavLei: []
    });

    const t = (key: string) => getLanguageObject(language)[key] || key;
    const formatNumber = (value: number) => new Intl.NumberFormat(language === 'en' ? 'en-GB' : 'cs-CZ').format(value);
    const formatDate = (value?: string) => (value ? dayjs(value).format('DD. MM. YYYY') : '');

    const countryName = (code: string) => {
        const country = cisStaty.find(item => item.value === code);
        return country ? t(country.labelcz) : code;
    };

    // filter the dropdown options by the label as it is displayed (translated, case-insensitive
    // substring match - antd's default only prefix-matches the raw option children)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const makeFilterOption = (list: ISelectData[]) => (input: string, option: any) => {
        const item = list.find(d => String(d.value) === String(option?.props?.value));
        if (!item) return false;
        const label = t(language === 'en' && item.labelen ? item.labelen : item.labelcz);
        return label.toLowerCase().includes(input.toLowerCase());
    };
    const filterCountries = makeFilterOption(cisStaty);
    const filterCategories = makeFilterOption(CATEGORY_OPTIONS);
    const filterStatuses = makeFilterOption(STATUS_OPTIONS);

    // the select is controlled with value=[], so antd cannot know what is already picked -
    // the picked options get their own class (highlight in the dropdown) and a data attribute
    // so a re-click can be intercepted and turns into a deselect (see onFilterOptionClick)
    const renderFilterOptions = (field: TFilterField) => (list: ISelectData[]) =>
        list.map(item => {
            const value = String(item.value);
            return (
                <Option
                    key={value}
                    value={value}
                    className={
                        chipValues[field].includes(value) ? 'lei-search-opt-selected' : 'select-option-component'
                    }
                    data-filter-value={value}
                >
                    <TranslateText>{language === 'en' && item.labelen ? item.labelen : item.labelcz}</TranslateText>
                </Option>
            );
        });

    // search text coming from the top menu (?q=...) - also when the user searches again while already on this page
    useEffect(() => {
        const q = new URLSearchParams(location.search).get('q')?.trim() ?? '';
        form.setFieldsValue({
            q
        });
        setFilters(prev =>
            prev.q === q
                ? prev
                : {
                      ...prev,
                      q
                  }
        );
        setPage(1);
    }, [location.search, form]);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(false);
        // any new search brings the table back (closes an open "Obnova"/"Převod" form)
        setRowAction(null);

        searchLeiRecords(filters, page, pageSize, sort, controller.signal)
            .then(result => {
                setRows(result.rows);
                setTotal(result.total);
                // warm up the flags of the loaded page during idle time (see services/lei/leiCountryFlag)
                preloadCountryFlags(result.rows.map(row => row.country));
            })
            .catch(err => {
                if (isLeiSearchCancelled(err)) return;
                setRows([]);
                setTotal(0);
                setError(true);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [filters, page, pageSize, sort]);

    // timer for the debounced fetch (spamming select options / chip X's = one API call)
    const filterTimer = useRef<ReturnType<typeof setTimeout>>();
    // last filter set that was actually applied - pressing search with no change must not refetch
    const appliedFilters = useRef<ILeiSearchFilters>({
        q: initialQ,
        jurisdikce: DEFAULT_JURISDICTION,
        kategorie: [],
        stavLei: []
    });

    // chips update immediately; the fetch is scheduled and debounced, so a burst of clicks
    // results in a single API call after the user stops clicking for FILTER_DEBOUNCE_MS
    const applyFilters = (chips: TChipValues, immediate = false) => {
        setChipValues(chips);
        if (filterTimer.current) clearTimeout(filterTimer.current);
        const commit = () => {
            filterTimer.current = undefined;
            const next: ILeiSearchFilters = {
                q: ((form.getFieldValue('q') as string | undefined) || '').trim(),
                ...chips
            };
            const prev = appliedFilters.current;
            if (
                prev.q === next.q &&
                prev.jurisdikce.join('|') === next.jurisdikce.join('|') &&
                prev.kategorie.join('|') === next.kategorie.join('|') &&
                prev.stavLei.join('|') === next.stavLei.join('|')
            ) {
                return; // nothing actually changed - skip the refetch
            }
            appliedFilters.current = next;
            setFilters(next);
            setPage(1);
        };
        if (immediate) commit();
        else filterTimer.current = setTimeout(commit, FILTER_DEBOUNCE_MS);
    };

    // make sure a pending debounced fetch does not fire after the page unmounted
    useEffect(
        () => () => {
            if (filterTimer.current) clearTimeout(filterTimer.current);
        },
        []
    );

    const onFinish = () => {
        // explicit button press - fetch right away
        applyFilters(chipValues, true);
    };

    const onFilterChange = (field: TFilterField) => (selected: string[]) => {
        // the selects are controlled with value=[] (always shown empty), so `selected`
        // contains only the freshly clicked value - merge it in, skip duplicates
        const next: TChipValues = {
            ...chipValues,
            [field]: [...chipValues[field], ...selected.filter(item => !chipValues[field].includes(item))]
        };
        applyFilters(next);
    };

    // X on a chip: remove just that value, the (debounced) search follows
    const onRemoveChip = (field: TFilterField, value: string) => {
        const next: TChipValues = {
            ...chipValues,
            [field]: chipValues[field].filter(item => item !== value)
        };
        applyFilters(next);
    };

    // clicking an option that is already picked removes it (toggle off) - antd itself would
    // just re-add it, since from the controlled select's point of view nothing is selected.
    // The dropdown is rendered inside .lei-search-filter-select (antd's default popup
    // container), so the capture-phase handler on that div sees the option's data attribute
    // before rc-select's own click listener runs.
    const onFilterOptionClick = (field: TFilterField) => (e: React.MouseEvent<HTMLDivElement>) => {
        const option = (e.target as HTMLElement).closest<HTMLElement>('[data-filter-value]');
        const value = option?.dataset?.filterValue;
        if (!value || !chipValues[field].includes(value)) return;
        e.preventDefault();
        e.stopPropagation();
        onRemoveChip(field, value);
    };

    const onClearFilters = () => {
        applyFilters({
            jurisdikce: DEFAULT_JURISDICTION,
            kategorie: [],
            stavLei: []
        });
    };

    const onTableChange: TableProps<ILeiSearchRow>['onChange'] = (_pagination, _filters, sorter) => {
        const current = Array.isArray(sorter) ? sorter[0] : sorter;
        setSort(
            current?.order && current.columnKey
                ? {
                      columnKey: String(current.columnKey),
                      order: current.order
                  }
                : undefined
        );
        setPage(1);
    };

    const columns: ColumnsType<ILeiSearchRow> = [
        {
            key: 'lei',
            dataIndex: 'lei',
            title: <TranslateText>LEI_SEARCH_COL_LEI</TranslateText>,
            width: 230,
            sorter: true,
            sortOrder: sort?.columnKey === 'lei' ? sort.order : null,
            render: (lei: string) => <span className="lei-search-code">{lei}</span>
        },
        {
            key: 'legalName',
            dataIndex: 'legalName',
            title: <TranslateText>LEI_SEARCH_COL_NAME</TranslateText>,
            sorter: true,
            sortOrder: sort?.columnKey === 'legalName' ? sort.order : null
        },
        {
            key: 'registeredAs',
            dataIndex: 'registeredAs',
            title: <TranslateText>LEI_SEARCH_COL_ICO</TranslateText>,
            width: 130,
            sorter: true,
            sortOrder: sort?.columnKey === 'registeredAs' ? sort.order : null
        },
        {
            key: 'country',
            dataIndex: 'country',
            title: <TranslateText>LEI_SEARCH_COL_COUNTRY</TranslateText>,
            width: 170,
            sorter: true,
            sortOrder: sort?.columnKey === 'country' ? sort.order : null,
            render: (code: string) =>
                code ? (
                    <span className="lei-search-country">
                        <CountryFlag code={code} />
                        {countryName(code)}
                    </span>
                ) : null
        },
        {
            key: 'registrationStatus',
            dataIndex: 'registrationStatus',
            title: <TranslateText>LEI_SEARCH_COL_STATUS</TranslateText>,
            width: 140,
            sorter: true,
            sortOrder: sort?.columnKey === 'registrationStatus' ? sort.order : null,
            render: (status: string) =>
                status ? <Tag color={statusTagColor(status)}>{t(`LEI_STATUS_${status}`)}</Tag> : null
        },
        {
            key: 'nextRenewalDate',
            dataIndex: 'nextRenewalDate',
            title: <TranslateText>LEI_SEARCH_COL_RENEWAL</TranslateText>,
            width: 170,
            sorter: true,
            sortOrder: sort?.columnKey === 'nextRenewalDate' ? sort.order : null,
            render: (date?: string) => formatDate(date)
        },
        {
            key: 'action',
            title: <TranslateText>LEI_SEARCH_COL_ACTION</TranslateText>,
            width: 80,
            align: 'center',
            render: (_: unknown, row: ILeiSearchRow) => {
                const items = [
                    {
                        key: 'detail',
                        label: <TranslateText>LEI_SEARCH_ACTION_DETAIL</TranslateText>
                    }
                ];

                // LEIs managed by our own LOU (CDCP) -> renew, the other ones (but only from
                // accredited jurisdictions) -> transfer to us
                if (row.managingLou === CDCP_LOU_LEI) {
                    items.push({
                        key: 'obnova',
                        label: <TranslateText>LEI_SEARCH_ACTION_OBNOVA</TranslateText>
                    });
                } else if (row.jurisdiction && leiCountrySelect.some(item => item.value === row.jurisdiction)) {
                    items.push({
                        key: 'prevod',
                        label: <TranslateText>LEI_SEARCH_ACTION_PREVOD</TranslateText>
                    });
                }

                return (
                    <Dropdown
                        trigger={['click']}
                        menu={{
                            items,
                            onClick: ({ key }) => {
                                if (key === 'detail') {
                                    // TODO: navigate to the LEI detail page once it is added
                                } else if (key === 'obnova') {
                                    setRowAction({
                                        lei: row.lei,
                                        typ: ETypZadosti.AKTUALIZACE
                                    });
                                } else if (key === 'prevod') {
                                    setRowAction({
                                        lei: row.lei,
                                        typ: ETypZadosti.PREVOD
                                    });
                                }
                            }
                        }}
                    >
                        <Button
                            type="text"
                            className="lei-search-row-action"
                            icon={<RightOutlined />}
                            aria-label={t('LEI_SEARCH_COL_ACTION')}
                        />
                    </Dropdown>
                );
            }
        }
    ];

    const from = total ? (page - 1) * pageSize + 1 : 0;
    const to = total ? Math.min(page * pageSize, total) : 0;

    // one removable chip per selected value, rendered right next to its filter select
    const renderChips = (field: TFilterField, values: string[], labelOf: (value: string) => string) =>
        values.map(value => (
            <Tag
                key={`${field}:${value}`}
                className="lei-search-chip"
                closable={true}
                onClose={e => {
                    // we own the removal (and the refetch) - stop antd from hiding it on its own
                    e.preventDefault();
                    onRemoveChip(field, value);
                }}
            >
                {labelOf(value)}
            </Tag>
        ));

    return (
        <>
            <LeiLoginlessMenu />

            <section className="lei-search-hero">
                <div className="lei-search-hero-inner">
                    <h1>
                        <TranslateText>LEI_SEARCH_TITLE</TranslateText>
                    </h1>
                    <p>
                        <TranslateText>LEI_SEARCH_SUBTITLE</TranslateText>
                    </p>

                    <div className="lei-search-card">
                        <Form
                            form={form}
                            className="lei-search-form"
                            initialValues={{
                                q: initialQ
                            }}
                            onFinish={onFinish}
                        >
                            <div className="lei-search-row">
                                <Form.Item name="q" className="lei-search-input">
                                    <Input placeholder={t('LEI_SEARCH_INPUT_PLACEHOLDER')} allowClear={true} />
                                </Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    id="vyhledatBtn"
                                    className="lei-search-submit"
                                    icon={<ArrowRightOutlined />}
                                >
                                    <TranslateText>LEI_SEARCH_BTN</TranslateText>
                                </Button>
                            </div>

                            <div className="lei-search-row lei-search-filters">
                                <div className="lei-search-filter-list">
                                    <div className="lei-search-filter">
                                        <div
                                            className="lei-search-filter-select"
                                            onClickCapture={onFilterOptionClick('jurisdikce')}
                                        >
                                            <SelectComponent
                                                mode="multiple"
                                                showSearch={true}
                                                data={cisStaty}
                                                filterOption={filterCountries}
                                                placeholder="LEI_SEARCH_FILTER_JURISDICTION"
                                                value={[]}
                                                onChange={onFilterChange('jurisdikce')}
                                                renderOptions={renderFilterOptions('jurisdikce')}
                                            />
                                        </div>
                                        {renderChips('jurisdikce', chipValues.jurisdikce, countryName)}
                                    </div>
                                    <div className="lei-search-filter">
                                        <div
                                            className="lei-search-filter-select"
                                            onClickCapture={onFilterOptionClick('kategorie')}
                                        >
                                            <SelectComponent
                                                mode="multiple"
                                                showSearch={true}
                                                data={CATEGORY_OPTIONS}
                                                filterOption={filterCategories}
                                                placeholder="LEI_SEARCH_FILTER_CATEGORY"
                                                value={[]}
                                                onChange={onFilterChange('kategorie')}
                                                renderOptions={renderFilterOptions('kategorie')}
                                            />
                                        </div>
                                        {renderChips('kategorie', chipValues.kategorie, value =>
                                            t(`LEI_CATEGORY_${value}`)
                                        )}
                                    </div>
                                    <div className="lei-search-filter">
                                        <div
                                            className="lei-search-filter-select"
                                            onClickCapture={onFilterOptionClick('stavLei')}
                                        >
                                            <SelectComponent
                                                mode="multiple"
                                                showSearch={true}
                                                data={STATUS_OPTIONS}
                                                filterOption={filterStatuses}
                                                placeholder="LEI_SEARCH_FILTER_STATUS"
                                                value={[]}
                                                onChange={onFilterChange('stavLei')}
                                                renderOptions={renderFilterOptions('stavLei')}
                                            />
                                        </div>
                                        {renderChips('stavLei', chipValues.stavLei, value => t(`LEI_STATUS_${value}`))}
                                    </div>
                                </div>
                                <Button id="vymazatFiltryBtn" className="lei-search-clear" onClick={onClearFilters}>
                                    <TranslateText>LEI_SEARCH_CLEAR_FILTERS</TranslateText>
                                </Button>
                            </div>
                        </Form>
                    </div>
                </div>
            </section>

            <main className="lei-search-results">
                {error && (
                    <Alert
                        type="error"
                        showIcon={true}
                        className="lei-search-alert"
                        message={<TranslateText>LEI_SEARCH_ERROR</TranslateText>}
                    />
                )}

                {total !== undefined && !error && (
                    <div className="lei-search-summary">
                        <strong>
                            <TranslateText>LEI_SEARCH_RESULTS_TITLE</TranslateText>:
                        </strong>{' '}
                        <TranslateText>LEI_SEARCH_FOUND</TranslateText> {formatNumber(total)}{' '}
                        <TranslateText>{resultWordKey(total)}</TranslateText>
                    </div>
                )}

                {rowAction ? (
                    <div className="lei-search-no-results">
                        <LeiZadostForm types={[rowAction.typ]} typ={rowAction.typ} initialLei={rowAction.lei} />
                    </div>
                ) : total === 0 && !error && !loading ? (
                    <div className="lei-search-no-results">
                        <LeiZadostForm types={[ETypZadosti.REGISTRACE]} />
                    </div>
                ) : (
                    <div className="lei-search-table-wrap">
                        <Table<ILeiSearchRow>
                            rowKey="lei"
                            className="lei-search-table"
                            columns={columns}
                            dataSource={rows}
                            loading={loading}
                            pagination={false}
                            onChange={onTableChange}
                            scroll={{
                                x: 960
                            }}
                            locale={{
                                emptyText: <TranslateText>LEI_SEARCH_NO_RESULTS</TranslateText>
                            }}
                        />

                        <div className="lei-search-table-footer">
                            <div className="lei-search-page-size">
                                <span>
                                    <TranslateText>LEI_SEARCH_PAGE_SIZE</TranslateText>
                                </span>
                                <Select
                                    value={pageSize}
                                    options={PAGE_SIZE_OPTIONS.map(size => ({
                                        value: size,
                                        label: size
                                    }))}
                                    onChange={(size: number) => {
                                        setPageSize(size);
                                        setPage(1);
                                    }}
                                />
                            </div>

                            <Pagination
                                current={page}
                                pageSize={pageSize}
                                total={Math.min(total ?? 0, GLEIF_MAX_RECORDS)}
                                showSizeChanger={false}
                                hideOnSinglePage={true}
                                onChange={setPage}
                            />

                            <span className="lei-search-range">
                                {from} - {to} <TranslateText>LEI_SEARCH_RANGE_OF</TranslateText>{' '}
                                {formatNumber(total ?? 0)}
                            </span>
                        </div>
                    </div>
                )}

                {(total ?? 0) > GLEIF_MAX_RECORDS && (
                    <p className="lei-search-limit-hint">
                        <TranslateText>LEI_SEARCH_LIMIT_HINT</TranslateText>
                    </p>
                )}
            </main>

            <LeiLoginlessFooter />
        </>
    );
};

export default LeiLoginlessSearch;
